import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";

import { Dialog as DialogPrimitive } from "radix-ui";

import { useTouchRipple } from "@/components/touch-ripple";
import { DialogPortalContainerContext } from "@/components/ui/dialog-portal-container";
import { triggerOf } from "@/components/ui/icon-menu-context";
import { MuiButton, type MuiButtonProps } from "@/components/ui/mui-button";
import { muiGlyph } from "@/components/ui/mui-glyph";
import { MuiTypography } from "@/components/ui/mui-typography";
import { cn } from "@/lib/utils";

// The donor's dialog shell, ported once for every dialog on the migrated
// surfaces.
//
// Source: `packages/react-ui/src/dialog/*` in the modsy monorepo — a wrapper
// over MUI's `Dialog` that fixes the chrome every Spec Tool dialog wears, which
// is exactly why it ports here as a shared component rather than as classes on
// the vendored primitive (`docs/agents/migration-guidelines.md`, "Shared donor
// components port as shared Atlas components"). The measurements *are* the spec:
//
//   - paper: 16px corner radius, `fullWidth` (by default), MUI's `maxWidth` —
//     `sm` is 600px, `lg` is 1200px. Those two are the sizes the ported
//     dialogs ask for.
//   - titlebar: 40px left/right (`paddingX: 5`), 24px top, 16px bottom; the
//     title is an `h2` at 32px / 1.25 line height / weight 400 — *not* bold, and
//     that is the donor's `dialogTitle` variant, not an accident;
//   - content: 40px left/right, 24px top and bottom, and it is the part that
//     scrolls when the body outgrows the viewport;
//   - actions: 40px left/right, 24px top and bottom, right-aligned;
//   - buttons: MUI `size="large"` with a 100px minimum width.
//
// The vendored shadcn `dialog.tsx` underneath supplies the Radix behavior
// (portal, overlay, focus trap, escape); none of its own padding survives,
// because none of it is the donor's. This composes the Radix parts directly
// rather than the vendored `DialogContent` so the close button can sit *inside*
// the titlebar's flow instead of floating over the body, which is where the
// donor puts it.

/**
 * How the paper and its scrim arrive and leave — **one clock for both**.
 *
 * MUI's `Dialog` hands the same `transitionDuration` to the `Fade` around the
 * paper and to the backdrop slot (`Dialog.js:322,328,339`), and the donor's
 * shell passes none of its own (`packages/react-ui/src/dialog/dialog.tsx:113-143`),
 * so the theme's defaults stand: `enteringScreen` 225ms in, `leavingScreen`
 * 195ms out (`createTransitions.js:36,38`), eased `easeInOut` — which is
 * Tailwind's `ease-in-out` to the digit. The two therefore move as one element.
 *
 * A single `duration-200` on the paper and tw-animate-css's own 150ms default
 * on the scrim was the visible version of that: the scrim locked in ~50ms ahead
 * of the dialog and cleared out from under it on the way back.
 */
const SHELL_CLOCK =
  "ease-in-out data-[state=open]:duration-[225ms] data-[state=closed]:duration-[195ms]";

const FADE_IN = "data-[state=open]:animate-in data-[state=open]:fade-in-0";

/** Without it Radix finds no animation on the close and unmounts at once. */
const FADE_OUT = "data-[state=closed]:animate-out data-[state=closed]:fade-out-0";

const SIZES = {
  /** MUI `maxWidth="sm"`. */
  sm: "max-w-[600px]",
  /** MUI `maxWidth="md"` — the donor theme's `md` breakpoint, 900px. */
  md: "max-w-[900px]",
  /** MUI `maxWidth="lg"`. */
  lg: "max-w-[1200px]",
} as const;

/**
 * The same caps for a paper that is **not** `fullWidth`: MUI sizes it to its
 * content (`width: auto` in a centring flex box) and caps it at the breakpoint
 * — or at `calc(100% - 64px)` once the viewport is narrower than the breakpoint
 * plus the paper's 32px margins (`Dialog.js:139-178`), which is one `min()`.
 */
const CONTENT_SIZES = {
  sm: "w-fit max-w-[min(600px,calc(100%-4rem))]",
  md: "w-fit max-w-[min(900px,calc(100%-4rem))]",
  lg: "w-fit max-w-[min(1200px,calc(100%-4rem))]",
} as const;

/**
 * `@mui/icons-material/Close`, the glyph `DialogCloseButton` draws: one filled
 * path at 1em of the `IconButton`'s 24px, square-cornered. Lucide's `XIcon` was
 * two round-capped strokes in its place (#718 gap 6).
 */
const MuiDialogCloseIcon = muiGlyph(
  "mui-dialog-close",
  "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  "size-6",
);

/**
 * The donor's `DialogCloseButton` — an MUI `IconButton` (40px box, 24px glyph)
 * in `grey[500]`, pulled 8px past its container's padding
 * (`marginInlineEnd: -1`) so the glyph, not the hit area, lines up with the
 * 40px gutter.
 */
function DialogCloseButton({ className, label = "Close" }: { className?: string; label?: string }) {
  // **Centred**, because MUI's `IconButton` renders its `ButtonBase` with
  // `centerRipple` — the ink blooms from the middle of the 40px box rather than
  // washing to its furthest corner from wherever the pointer landed.
  //
  // The X is the one part of this shell the donor leaves rippling: it is a bare
  // `MuiIconButton` carrying only `sx` and an `aria-label`
  // (`packages/react-ui/src/dialog/dialog-close-button.tsx:40-51`), so
  // `ButtonBase` inks it like any other icon button, where the action row two
  // files over passes `disableRipple`. Confirmed live on modsy, both ways.
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ center: true });

  return (
    <DialogPrimitive.Close
      aria-label={label}
      {...rippleTargetProps}
      className={cn(
        // `relative` so the layer fills the button, and `overflow-hidden` so the
        // ink is clipped to the circle instead of spilling into the titlebar.
        "text-lennar-icon-muted relative -mr-2 flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full",
        // `IconButton.js:60-62` moves `background-color` alone, on the
        // `shortest` clock (150ms) — so the 4% hover wash fades rather than
        // snapping. Tailwind's default duration and easing are already MUI's.
        "transition-[background-color]",
        "hover:bg-lennar-action-hover focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
        className,
      )}
    >
      <MuiDialogCloseIcon />
      {rippleLayer}
    </DialogPrimitive.Close>
  );
}

interface AppDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: keyof typeof SIZES;
  /**
   * MUI's `fullWidth` — the paper runs to its size's cap. On by default: most
   * ported dialogs pass it. `false` is MUI's own default, a paper as wide as
   * its content up to the cap — the Create Visualization dialog's two info
   * dialogs, which pass neither (#755), and the per-plan manufacturer dialog
   * (`PerPlanManufacturerDialog.tsx:61-71`).
   */
  fullWidth?: boolean;
  /** The action row, already wrapped in `AppDialogActions`. */
  actions?: ReactNode;
  className?: string;
  /**
   * The id of the element inside `children` that describes this dialog, for a
   * caller whose own donor names one. Left unset, Radix's description is opted
   * out of — see the comment on `Content` below.
   */
  describedById?: string;
  /**
   * Where the focus goes on opening, if the donor puts it somewhere Radix
   * would not. Radix focuses the first tabbable child; MUI's trap focuses the
   * paper unless a child asked for it, so the two agree only by accident. A
   * caller whose donor carries `autoFocus` — or pointedly does not — takes the
   * event over (`event.preventDefault()`, then focus what the donor focuses).
   */
  onOpenAutoFocus?: (event: Event) => void;
  /**
   * Where the focus goes once the paper has gone, for a caller whose donor puts
   * it somewhere other than the dialog's opener. The shell already gives focus
   * back to the opener on the close (see `AppDialog`), which is every ported
   * donor's arrangement; this runs after that, at the unmount, and a caller that
   * focuses something here has the last word.
   */
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * Whether the close hands focus back to the opener — on, as MUI's trap does.
   * Off is the donor's `disableRestoreFocus` (the name-and-note form,
   * `GenericNameNoteDialogForm.tsx:40-51`): the focus is left where the
   * closing paper drops it.
   */
  restoreFocus?: boolean;
  /**
   * The close is an unmount, with no fade — for a donor that mounts its dialog
   * only while it has a subject (`{selected && <Dialog open … />}`), so the
   * paper and its scrim leave in the frame the subject clears, where MUI's own
   * close fades them over 195ms. The opening fade is unchanged.
   */
  closeInstantly?: boolean;
  /**
   * Hides the X, for a dialog that may not be dismissed for a while — Push
   * Changes while its push runs (#1147 D9 = b). Escape and the backdrop are
   * the caller's to make inert (`dismissGuard`); the X is drawn otherwise.
   */
  hideClose?: boolean;
  /**
   * A photo down the paper's left side — the donor's `splashImgUrl`, which the
   * Vis Spec dialog passes on its prepare stage alone. Drawn only for a
   * non-empty string, as the donor's `typeof … === 'string' && length > 0`.
   */
  splashImgUrl?: string;
  /** The photo's `max-width` in px — the donor's `splashImgSize`, 360 by default. */
  splashImgSize?: number;
  /**
   * The chrome around the content: react-ui's shell (the default), or `raw` —
   * the bare MUI `Dialog` the spec line form draws for itself
   * (`SpecSheetLineDialogForm.tsx:50-95`), measured on the running donor
   * (#807 E1–E4): the title alone at `24px 40px`, the X — named `close` —
   * pinned to the paper's corner at `top 24, right 32` rather than in the
   * titlebar's flow, and the content at `16px 40px 40px`.
   */
  frame?: "shell" | "raw";
  /**
   * The donor's `sx.zIndex` on its dialog root, for a dialog that must sit
   * above a snackbar — "Where to save"'s `zIndex.snackbar + 1`, 1401, over the
   * target-cost toast's 1400 (#1217). The overlay and the paper take it
   * together; left unset, both stay on the app's modal layer.
   */
  zIndex?: number;
  /** A `data-testid`. react-ui spreads it on MUI's dialog root, which has no
   *  element here, so it marks the paper. */
  "data-testid"?: string;
  children: ReactNode;
}

/**
 * Where the dialog's name lives — a union, not two independent props, because
 * Radix wants exactly one `Dialog.Title` and the two spellings are exclusive:
 * a titlebar dialog with no `title` renders an empty one, and a `titleInBody`
 * dialog that also passed `title` would render two.
 */
export type AppDialogProps = AppDialogBaseProps &
  (
    | {
        /** The titlebar's text, and the dialog's accessible name. */
        title: string;
        /**
         * The line under the title — the donor titlebar's `DialogContentText`,
         * stacked beneath the title beside the X (`dialog-titlebar.tsx`), in
         * the body's tone and size.
         */
        subtitle?: string;
        /**
         * Drawn on the title's line, 12px after it and centred on it — "Where
         * to save"'s refreshing spinner (#1243 D2). Never part of the name.
         */
        titleAdornment?: ReactNode;
        titleInBody?: false;
      }
    | {
        title?: never;
        subtitle?: never;
        titleAdornment?: never;
        /**
         * Drops the titlebar, the way the donor's success dialog does. The body
         * then renders an `<AppDialogTitle>` of its own.
         */
        titleInBody: true;
      }
  );

export function AppDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  titleAdornment,
  titleInBody = false,
  size = "sm",
  fullWidth = true,
  actions,
  className,
  describedById,
  onOpenAutoFocus,
  onCloseAutoFocus,
  restoreFocus = true,
  closeInstantly = false,
  hideClose = false,
  splashImgUrl,
  splashImgSize = 360,
  frame = "shell",
  zIndex,
  "data-testid": testId,
  children,
}: AppDialogProps) {
  const showSplashImage = typeof splashImgUrl === "string" && splashImgUrl.length > 0;
  const [content, setContent] = useState<HTMLDivElement | null>(null);

  // **Focus goes back to whatever held it when the dialog took it, on the
  // close** (#718 gap 3). MUI's `FocusTrap` records the `relatedTarget` of the
  // first focus into the dialog — the element focus left, whether the trap
  // focused its paper or a child's `autoFocus` got there first — and restores it
  // from its `[open]` effect's cleanup, the commit `open` turns false in, while
  // the paper still has its 195ms fade to run (`FocusTrap.js:144-151,
  // 291-307`). Radix's modal dialog hands focus to its own `Dialog.Trigger` and
  // nowhere else, at the unmount; no ported dialog has one, so every catalog
  // dialog closed onto `<body>`.
  //
  // `undefined` is "not recorded yet this opening"; `null` is a recorded
  // nothing — focus came from the body — which restores nothing, as MUI's null
  // `relatedTarget` does.
  //
  // **A dialog opened from a menu row records the menu's trigger**, which is
  // what the donor's records: MUI batches the row's handler with the menu's
  // close, the menu's trap hands focus back to its trigger first, and the
  // dialog's `Portal` mounts a pass later, taking focus from the trigger. Radix
  // runs a row's `onSelect` inside `flushSync`, so here the dialog commits and
  // takes focus from the *row*, which is gone by the time the dialog closes.
  // The row is resolved to its menu's trigger through the link Radix already
  // draws between them — the paper's `aria-labelledby` is the trigger's `id`.
  //
  // A passive effect, not a layout one: Radix's focus scope stays armed until
  // its own `trapped` effect is cleaned up, which is a passive cleanup of this
  // same commit, and a focus moved out before then is pulled straight back
  // into the closing paper. Every passive cleanup runs before any passive
  // setup, so this lands after the trap has let go — and still in the close
  // commit, as MUI's does.
  const opener = useRef<HTMLElement | null | undefined>(undefined);
  const wasOpen = useRef(open);
  useEffect(() => {
    const closing = wasOpen.current && !open;
    wasOpen.current = open;
    if (!closing) return;
    const node = opener.current;
    opener.current = undefined;
    if (restoreFocus && node?.isConnected) node.focus();
  }, [open, restoreFocus]);

  // **The backdrop closes on the click, not the press** — MUI's `Dialog`
  // (`Dialog.js:255-272`): a mousedown on the backdrop itself arms it, and the
  // click that follows closes, wherever the release lands; a press that began
  // on the paper never does. Radix dismisses on the pointerdown outside, so a
  // press held on the backdrop faded the dialog out from under it. Every
  // mousedown disarms first, in the capture phase, before the backdrop's own
  // re-arms it.
  const pressedOnBackdrop = useRef(false);
  const latestOnOpenChange = useRef(onOpenChange);
  useEffect(() => {
    latestOnOpenChange.current = onOpenChange;
  });
  useEffect(() => {
    if (!open) return;
    const disarm = () => {
      pressedOnBackdrop.current = false;
    };
    const fire = () => {
      if (!pressedOnBackdrop.current) return;
      pressedOnBackdrop.current = false;
      latestOnOpenChange.current(false);
    };
    document.addEventListener("mousedown", disarm, true);
    document.addEventListener("click", fire);
    return () => {
      document.removeEventListener("mousedown", disarm, true);
      document.removeEventListener("click", fire);
    };
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-slot="app-dialog-overlay"
          onMouseDown={(event) => {
            if (event.button === 0) pressedOnBackdrop.current = true;
          }}
          style={{ zIndex }}
          className={cn(
            "fixed inset-0 z-50 bg-black/50",
            FADE_IN,
            !closeInstantly && FADE_OUT,
            SHELL_CLOCK,
          )}
        />
        {/* `aria-describedby={undefined}` by default: the donor's shared
            `Dialog` has no description element — its intro copy is body
            content — so Radix's description is opted out of explicitly rather
            than left missing. A caller whose own donor *does* name one
            (`confirm-delete-dialog.tsx:29`) passes `describedById`. */}
        <DialogPrimitive.Content
          ref={setContent}
          data-slot="app-dialog-content"
          style={{ zIndex }}
          data-testid={testId}
          aria-describedby={describedById}
          onOpenAutoFocus={onOpenAutoFocus}
          // The close's focus is the shell's (the opener restore above), so
          // Radix's unmount-time autofocus never gets a second say — today it
          // would reach only a `Dialog.Trigger`, which no caller renders.
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            event.preventDefault();
          }}
          onFocusCapture={(event) => {
            if (opener.current !== undefined) return;
            const from = event.relatedTarget;
            opener.current =
              from instanceof HTMLElement && !content?.contains(from) ? menuTriggerOf(from) : null;
          }}
          // Never on the press: the backdrop's click closes it (above). That
          // also keeps a press on a snackbar — which MUI draws above its
          // modal's backdrop — from closing anything.
          onPointerDownOutside={(event) => event.preventDefault()}
          // An Escape inside a select's menu is the menu's: MUI stacks the
          // menu's modal over the dialog's, and only the top one answers.
          // Radix hears Escape on the document before the menu does.
          onEscapeKeyDown={(event) => {
            const target = event.target;
            if (target instanceof Element && target.closest('[data-slot="mui-menu-select-root"]'))
              event.preventDefault();
          }}
          // MUI's trap sends Shift+Tab from the paper to its start sentinel,
          // which focuses the last tabbable inside (`FocusTrap.js:248-262,
          // 310-318`); Radix lets it leave and pulls focus back to the paper.
          onKeyDown={(event) => {
            // **An Escape from inside the dialog that no layer took closes
            // it**: the case where a menu inside the dialog is still leaving.
            // The donor's `Menu` leaves MUI's modal stack the moment it closes
            // (no `closeAfterTransition`, `useModal.js:100-101`), so a second
            // Escape during its exit bubbles on to this dialog's modal root,
            // now the top modal, and closes it (`useModal.js:104-121`). Radix
            // keeps the closing menu's layer on top until its paper unmounts,
            // and this dialog's own layer stands down beneath it. The menu lets
            // the key past unconsumed (`spareFromRadixDismiss`), and this is
            // where it lands.
            //
            // A React handler, as MUI's is, so it hears what bubbles through
            // the React tree: a row of the leaving paper, portalled out of
            // this node, as well as the trigger in it. It runs after Radix's
            // capture-phase document listeners and after every handler below
            // it, and only on an undefaulted key. So every layer that does
            // answer Escape has already consumed it, and this stands down:
            // this dialog's own layer when it is on top, a menu or select open
            // inside it, or a dialog stacked over it.
            //
            // **Never from inside a layer still open**: a key from there is
            // that layer's. MUI's open select menu is the top modal and stops
            // the key (`useModal.js:115-120`). Here, a tooltip standing over
            // an open popover spares the key (`MuiTooltip`), and the popover,
            // not being Radix's top layer, hears nothing. A leaving menu is
            // `data-state="closed"`, off the stack, and still passes.
            //
            // **Nor from inside a dialog stacked over this one**, closing or
            // not. Its own layer consumes the key while it is open. Through
            // its fade it is still MUI's top modal (`Dialog` closes after the
            // transition), so the key is its own either way.
            if (event.key === "Escape") {
              if (event.nativeEvent.defaultPrevented || event.nativeEvent.isComposing) return;
              const target = event.target instanceof Element ? event.target : null;
              const paper = target?.closest("[data-slot=app-dialog-content]");
              if (paper && paper !== event.currentTarget) return;
              const layer = target?.closest("[data-radix-popper-content-wrapper] > [data-state]");
              if (layer?.getAttribute("data-state") === "open") return;
              event.preventDefault();
              onOpenChange(false);
              return;
            }
            if (event.key !== "Tab" || !event.shiftKey || event.target !== event.currentTarget)
              return;
            const last = lastTabbable(event.currentTarget);
            if (last === null) return;
            event.preventDefault();
            last.focus();
          }}
          className={cn(
            // Centred by auto margins in the fixed inset box, MUI's flex centring
            // by other means: a `translate(-50%)` puts an odd-height paper on
            // a half pixel, and its text a pixel off modsy's.
            "shadow-mui-elevation-24 fixed inset-0 z-50 m-auto flex h-fit max-h-[calc(100vh-4rem)] flex-col rounded-2xl bg-white outline-none",
            FADE_IN,
            !closeInstantly && FADE_OUT,
            SHELL_CLOCK,
            // **The clock is the fade's alone.** A `duration-*` with no
            // `transition-property` transitions `all` (its initial value), so a
            // paper whose size changed while open — the Create Visualization
            // dialog's stages (#755) — eased between widths over 225ms where
            // MUI's paper snaps. The fade is a keyframe animation and keeps it.
            "transition-none",
            fullWidth ? ["w-[calc(100%-4rem)]", SIZES[size]] : CONTENT_SIZES[size],
            className,
          )}
        >
          <DialogPortalContainerContext.Provider value={content}>
            <SplashLayout url={showSplashImage ? splashImgUrl : undefined} size={splashImgSize}>
              {titleInBody ? null : (
                <div
                  data-slot="app-dialog-titlebar"
                  // 8px between title and X (`dialog-titlebar.tsx`'s
                  // `spacing={1}`), not the 16px a `gap-4` would give.
                  className={
                    frame === "raw"
                      ? // `DialogTitle sx={{ paddingX: 5, paddingY: 3 }}`: the
                        // title alone, as wide as the paper's text, the X
                        // floating over it.
                        "shrink-0 px-10 py-6"
                      : "flex shrink-0 items-start justify-between gap-2 px-10 pt-6 pb-4"
                  }
                >
                  {subtitle === undefined ? (
                    <TitleLine adornment={titleAdornment}>{title}</TitleLine>
                  ) : (
                    // `VStack alignItems='stretch' sx={{ flex: 'auto' }}` around
                    // the title and its `DialogContentText`, no gap between them.
                    <div className="flex flex-auto flex-col">
                      <TitleLine adornment={titleAdornment}>{title}</TitleLine>
                      <AppDialogContentText>{subtitle}</AppDialogContentText>
                    </div>
                  )}
                  {frame === "shell" && !hideClose ? <DialogCloseButton /> : null}
                </div>
              )}
              <div
                data-slot="app-dialog-body"
                className={cn(
                  "min-h-0 flex-1 overflow-y-auto px-10",
                  // `DialogContent sx={{ padding: 5, paddingTop: '16px !important' }}`.
                  frame === "raw" ? "pt-4 pb-10" : "py-6",
                )}
              >
                {/* A titlebar-less dialog still closes from a corner X — the donor
                floats it over the content at 24px / 40px rather than dropping
                it (`dialog.tsx`, the `showTitlebar === false` branch).

                **Pinned to the paper, not to this scroller.** The donor's X is
                a DOM child of `DialogContent` too, but `DialogContent` is not
                positioned, so the X's containing block is the Paper
                (`position: relative`, `Dialog.js:116`): it neither scrolls with
                the body nor clips to it. This body is therefore left
                unpositioned, and the X anchors to the `fixed` content above it
                — the paper. It was `relative` here, and the X scrolled away
                with the content (#718 B2). */}
                {(titleInBody || frame === "raw") && !hideClose ? (
                  <DialogCloseButton
                    className="absolute top-6 right-10"
                    label={frame === "raw" ? "close" : undefined}
                  />
                ) : null}
                {children}
              </div>
              {actions}
            </SplashLayout>
          </DialogPortalContainerContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * The paper's inner layout with a splash photo — the donor's two `Stack`s
 * (`dialog.tsx:144-159`): a **row** at `flex: 1; overflow: hidden` holding the
 * `<img>` and then the dialog's own column at the same `flex: 1; overflow:
 * hidden`. Without a photo it is nothing at all, so every dialog that passes
 * none keeps exactly the DOM it had.
 *
 * The `<img>` carries the donor's inline style verbatim — `max-width: {size}px;
 * height: auto; flex: 0; object-fit: cover` (`:148-155`), not `sx`. Under those
 * rules the image's automatic minimum width is its intrinsic width capped at
 * `max-width`, the row's default `align-items: stretch` runs it to the paper's
 * height, and `cover` crops it. **Decorative** (`alt=""`): the donor passes no
 * `alt` at all, and the dialog is named by its title. A ruled parity break
 * (#741 D1): a screen reader skips the photo rather than reading its URL, and
 * a photo that fails to load draws nothing rather than the browser's
 * broken-image glyph.
 *
 * **Clipped to the paper's 16px corners.** MUI's paper is `overflow: hidden`
 * under `scroll='paper'` (`dialog.tsx:139`), so the photo's square corners are
 * cut by the paper's radius. Atlas's paper does not clip — its floating X and
 * every existing dialog depend on that — so the row clips instead, inheriting
 * the radius it fills.
 *
 * The titlebar's X stays in the titlebar, in the right-hand column; the body is
 * still the one scroller; a `titleInBody` dialog's floating X still anchors to
 * the paper, which this does not position.
 */
function SplashLayout({
  url,
  size,
  children,
}: {
  url: string | undefined;
  size: number;
  children: ReactNode;
}) {
  if (url === undefined) return children;
  return (
    <div
      data-slot="app-dialog-splash-row"
      className="flex min-h-0 flex-1 flex-row overflow-hidden rounded-[inherit]"
    >
      <img
        data-slot="app-dialog-splash"
        src={url}
        alt=""
        style={{ maxWidth: `${size}px`, height: "auto", flex: 0, objectFit: "cover" }}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </div>
  );
}

/** The title, with its adornment 12px after it when there is one. */
function TitleLine({ adornment, children }: { adornment: ReactNode; children: ReactNode }) {
  if (adornment === undefined || adornment === null || adornment === false) {
    return <AppDialogTitle>{children}</AppDialogTitle>;
  }
  return (
    <div className="flex items-center gap-3">
      <AppDialogTitle>{children}</AppDialogTitle>
      {adornment}
    </div>
  );
}

/**
 * The dialog's name — the donor's `h2` at 32px / 1.25 / weight 400.
 *
 * Exported because a `titleInBody` dialog renders it itself: Radix wants exactly
 * one `Dialog.Title` in the content, and a hidden one beside a visible heading
 * would give the dialog two.
 */
export function AppDialogTitle({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <DialogPrimitive.Title
      // `-0.5px` of tracking, which the donor's `h2` carries explicitly
      // (`packages/react-ui/src/theme/theme.ts:138-143`) and live modsy
      // computes on the dialog title. Not the same case as the buttons below,
      // whose `typography.button` carries none under Inter — this is a theme
      // override rather than an MUI default, so it applies whatever the font.
      className={cn(
        "text-lennar-ink text-[2rem] leading-[1.25] font-normal tracking-[-0.5px]",
        className,
      )}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

/** The donor's right-aligned action row: 40px sides, 24px top and bottom. */
export function AppDialogActions({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-slot="app-dialog-actions"
      className={cn("flex shrink-0 items-center justify-end gap-2 px-10 py-6", className)}
    >
      {children}
    </div>
  );
}

/** A footer button's props: the dialog fixes its variant and size. */
type DialogButtonProps = Omit<MuiButtonProps, "variant" | "size">;

/**
 * The donor's `PrimaryDialogButton` — MUI contained, `size="large"`, 100px floor.
 *
 * The shape is the shared `MuiButton`'s, exactly as its outlined sibling's
 * below is. `size="lg"` alone was shadcn's button wearing the donor's colour: 14px over a 20px line in a 40px box with
 * 8px corners, where MUI's `sizeLarge` is 15px over 26.25px, `8px 22px` of
 * padding — so 42px tall — and 4px corners. Measured on live modsy's Delete
 * confirmation, which is 100x42.
 */
export function DialogPrimaryButton({ className, ...props }: DialogButtonProps) {
  return (
    <MuiButton
      variant="contained"
      size="large"
      // The donor's `DialogButton` passes `disableRipple`
      // (`packages/react-ui/src/dialog/dialog-buttons.tsx:11`) and both dialog
      // buttons go through it, so the action row is the one surface in the app
      // that stays dry under the finger — confirmed live on modsy, where a
      // press-and-hold on Cancel or Delete mounts no `.MuiTouchRipple-root`.
      disableRipple
      className={cn("min-w-[100px]", className)}
      {...props}
    />
  );
}

/**
 * The donor's `SecondaryDialogButton` — the same button, outlined.
 *
 * MUI's outlined **primary**: blue text over a half-alpha blue border. shadcn's
 * `outline` variant is a neutral border around foreground-coloured text, which
 * is a different button, so the donor's look comes from the shared `MuiButton`.
 */
export function DialogSecondaryButton({ className, ...props }: DialogButtonProps) {
  return (
    <MuiButton
      variant="outlined"
      size="large"
      // `DialogButton` again — see the note on the primary.
      disableRipple
      className={cn("min-w-[100px]", className)}
      {...props}
    />
  );
}

/**
 * The donor's `TertiaryDialogButton` (`dialog-buttons.tsx:32-42`) — the quiet
 * third action at the footer's left: the links dialog's Delete, the Vis Spec
 * dialog's Back.
 *
 * MUI's **text** button at `size="large"` with `disableRipple`, through the same
 * `DialogButton` as its siblings, so the same 42px — 8px of padding around
 * 15px over 1.75 — and 4px corners. Where it parts from them:
 *
 *   - **no 100px floor**: the donor sets `minWidth: 100` on the primary and
 *     secondary only, so this keeps MUI's own 64px;
 *   - **`text.secondary` ink** from its `sx`, over MUI's **primary** hover wash.
 *     The donor passes `color={undefined}`, which MUI destructures to
 *     `'primary'` (`Button.js:496`), so the 4% under the pointer is
 *     `alpha(primary.main, 0.04)` — `MuiButton`'s text hover — not grey;
 *   - **disabled, it goes to `primary.main`** at the theme's half opacity: the
 *     theme's `&.Mui-disabled` rule puts the variant's own palette colour back
 *     and outranks the `sx` ink (`theme.ts:322-362`).
 *
 * `startIcon` is drawn to MUI's large `startIcon` geometry — a span at
 * `margin-right: 8px; margin-left: -4px`, its glyph at 22px (`Button.js:72-80,
 * 304-307`) — because both donor callers pass one (`DeleteIcon`,
 * `ArrowBackIcon`).
 */
export function DialogTertiaryButton({
  startIcon,
  className,
  children,
  ...props
}: DialogButtonProps & { startIcon?: ReactNode }) {
  return (
    <MuiButton
      variant="text"
      size="large"
      disableRipple
      className={cn("text-lennar-ink-secondary disabled:text-lennar-blue-600", className)}
      {...props}
    >
      {startIcon ? (
        <span
          data-slot="dialog-button-start-icon"
          className="mr-2 -ml-1 inline-flex items-center [&_svg]:size-[22px]"
        >
          {startIcon}
        </span>
      ) : null}
      {children}
    </MuiButton>
  );
}

/**
 * The donor's `<Spacer />` after the tertiary button — a `<span>` at `flex: 1
 * 1 auto` (`spacer.tsx:20-35`) that pushes it to the row's left edge while the
 * secondary and primary stay right. It takes `DialogActions`' 8px sibling
 * margin like any child (`DialogActions.js:48`), which `AppDialogActions`'
 * `gap-2` reproduces.
 */
export function DialogActionsSpacer() {
  return <span data-slot="dialog-actions-spacer" className="flex-auto" />;
}

/**
 * The donor's dialog **body**: one tone and one size for everything inside it.
 *
 * That is what `DialogContentText` gives the donor — it renders at
 * `text.secondary` (`rgba(0,0,0,0.6)`, this token exactly) and every
 * `Typography` inside inherits both that and `body1`'s 16px over 24px. The gap
 * is the donor's `VStack spacing={2}`.
 *
 * A component rather than a class string because it is literally the same
 * element every time — the three catalog confirmations and the batch summary
 * all draw it (`client/src/components/ui/`, "a shared look is a wrapper
 * component").
 */
export function AppDialogBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("text-lennar-ink-secondary flex flex-col gap-4 text-base leading-6", className)}
      {...props}
    />
  );
}

/**
 * MUI's `DialogContentText`: `Typography` at `body1` in `text.secondary`,
 * margin 0 (`DialogContentText.js`), and a `<p>`.
 *
 * Where the donor puts a dialog's copy — each confirmation's question
 * (`confirm-delete-dialog.tsx:29`, `delete-item-dialog.tsx:49`,
 * `mark-core-item-dialog.tsx:90`), the request-item intro
 * (`RequestItemIsland.tsx:79`) — so its look is one component here rather than
 * a class string per dialog.
 */
export function AppDialogContentText(props: Omit<ComponentProps<"p">, "color">) {
  return <MuiTypography variant="body1" color="secondary" {...props} />;
}

/**
 * A list of names inside that body — the donor's bare `<ul>`.
 *
 * It is bare in the source (`delete-item-dialog.tsx:58-66`,
 * `mark-core-item-dialog.tsx:96-104`) and timmy resets lists nowhere —
 * `src/index.css` is print rules only and MUI's `CssBaseline` leaves lists
 * alone — so it keeps the UA defaults: a 40px inline start and 1em of block
 * margin, on top of the body's own gap. Tailwind's preflight zeroes both, so
 * both are restated here; `pl-5` was half the indent, and the missing margins
 * halved the separation from the line above.
 *
 * **No cap and no scroller of its own.** The bare `<ul>` sits inside
 * `DialogContent`, which is the scroll region (`dialog.tsx:169`), so a long
 * list grows the paper to its `calc(100% - 64px)` ceiling and the *body*
 * scrolls once. `max-h-56 overflow-y-auto` put a short inner scroller inside a
 * paper that then never grew.
 *
 * **`body1`, always.** A smaller run inside a row rides an inline `<span>`
 * (`mark-core-item-dialog.tsx:99` puts `variant='body2'` on a
 * `component='span'`), never the list: sizing the `<ul>` moves everything
 * derived from it — line boxes, disc markers, and `1em` resolving smaller.
 */
export function AppDialogBulletList({ className, ...props }: ComponentProps<"ul">) {
  return (
    <ul className={cn("my-[1em] list-disc ps-10 text-base leading-6", className)} {...props} />
  );
}

/**
 * The element to give focus back to for an opener: the opener itself, or — when
 * it is a row inside a dropdown menu's paper — that menu's trigger.
 */
function menuTriggerOf(opener: HTMLElement): HTMLElement {
  const paper = opener.closest<HTMLElement>("[data-slot=dropdown-menu-content]");
  return (paper && triggerOf(paper)) ?? opener;
}

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The last element Tab can reach inside `root`, in document order. */
function lastTabbable(root: HTMLElement) {
  const candidates = [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter(
    // A native control taken out of the order by `tabindex="-1"` matches its
    // tag's selector all the same; its `tabIndex` says what Tab does.
    (element) => element.tabIndex >= 0 && element.getClientRects().length > 0,
  );
  return candidates.at(-1) ?? null;
}
